import { useMemo, useState } from 'react';
import { Loader2, Plus, Search, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useSuppressionListQuery } from '@/client/api/campaigns/campaigns.query';
import {
  useAddSuppressionMutation,
  useRemoveSuppressionMutation,
} from '@/client/api/campaigns/campaigns.mutation';
import {
  formatEpoch,
  isTruthyFlag,
  SuppressionListParams,
  SuppressionScope,
} from '@/client/types/campaigns';
import { useListFilters } from '@/client/hooks/useListFilters';
import Pagination from '@/client/components/common/Pagination';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';

export default function CampaignSuppressionsPanel({ canWrite }: { canWrite: boolean }) {
  const {
    searchTerm,
    debouncedSearchTerm,
    filters,
    page,
    limit,
    handleSearch,
    handleFilterChange,
    handlePageChange,
    handleLimitChange,
    resetFilters,
  } = useListFilters<{ scope: string; active_only: string }>({
    initialFilters: { scope: '', active_only: '1' },
    initialLimit: 25,
    // Namespaced so this panel's URL state doesn't collide with sibling tabs.
    namespace: 'supp',
  });

  const [phone, setPhone] = useState('');
  const [scope, setScope] = useState<SuppressionScope>('sms');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  const listParams: SuppressionListParams = useMemo(
    () => ({
      limit,
      offset: (page - 1) * limit,
      ...(debouncedSearchTerm.trim() ? { phone: debouncedSearchTerm.trim() } : {}),
      ...(filters.scope ? { scope: filters.scope } : {}),
      ...(filters.active_only === '1' || filters.active_only === '0'
        ? { active_only: filters.active_only }
        : {}),
    }),
    [debouncedSearchTerm, filters, page, limit],
  );

  const { data, isLoading, isError } = useSuppressionListQuery(listParams);
  const add = useAddSuppressionMutation();
  const remove = useRemoveSuppressionMutation();

  const rows = data?.suppressions ?? [];
  const pagination = data?.pagination;

  const handleAdd = async () => {
    if (!phone.trim()) {
      toast.error('Phone is required (E.164)', { richColors: true });
      return;
    }
    try {
      await add.mutateAsync({
        body: {
          phone: phone.trim(),
          scope,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
        idempotencyKey: generateIdempotencyKey(),
      });
      toast.success('Suppression added', { richColors: true });
      setPhone('');
      setReason('');
      setNotes('');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to add suppression', { richColors: true });
    }
  };

  const handleRemove = async (psid: number) => {
    try {
      await remove.mutateAsync({
        psid,
        idempotencyKey: generateIdempotencyKey(),
      });
      toast.success('Suppression removed', { richColors: true });
    } catch (error: any) {
      toast.error(error?.message || 'Failed to remove', { richColors: true });
    }
  };

  return (
    <div className="space-y-4">
      {canWrite && (
        <div className="space-y-3 rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900">Add / re-activate suppression</h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+233201234567"
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value as SuppressionScope)}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="sms">SMS</option>
              <option value="voice">Voice</option>
              <option value="all">All</option>
            </select>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason"
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes"
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex justify-end">
            <Button type="button" disabled={add.isPending} onClick={handleAdd} className="gap-2">
              {add.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              Add
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-lg bg-white p-4 shadow">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Filter by phone…"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={filters.scope}
            onChange={(e) => handleFilterChange('scope', e.target.value)}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
          >
            <option value="">All scopes</option>
            <option value="sms">SMS</option>
            <option value="voice">Voice</option>
            <option value="all">All</option>
          </select>
          <select
            value={filters.active_only}
            onChange={(e) => handleFilterChange('active_only', e.target.value)}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
          >
            <option value="1">Active only</option>
            <option value="0">Inactive only</option>
            <option value="">Any</option>
          </select>
          {(searchTerm || filters.scope || filters.active_only !== '1') && (
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              <X className="h-4 w-4" />
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        {isLoading && (
          <div className="space-y-3 p-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}
        {isError && (
          <p className="p-8 text-center text-sm text-red-600">Failed to load suppressions.</p>
        )}
        {!isLoading && !isError && rows.length === 0 && (
          <p className="p-8 text-center text-sm text-gray-500">No suppressions found.</p>
        )}
        {!isLoading && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Phone</th>
                  <th className="px-4 py-3 font-medium">Scope</th>
                  <th className="px-4 py-3 font-medium">Reason</th>
                  <th className="px-4 py-3 font-medium">Active</th>
                  <th className="px-4 py-3 font-medium">Added</th>
                  {canWrite && <th className="px-4 py-3 font-medium" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => (
                  <tr key={row.psid}>
                    <td className="px-4 py-3 font-mono text-xs">{row.phone}</td>
                    <td className="px-4 py-3">{row.scope || '—'}</td>
                    <td className="max-w-[200px] truncate px-4 py-3">{row.reason || '—'}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                          isTruthyFlag(row.active)
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {isTruthyFlag(row.active) ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-600">
                      {formatEpoch(row.created_at)}
                    </td>
                    {canWrite && (
                      <td className="px-4 py-3 text-right">
                        {isTruthyFlag(row.active) && (
                          <button
                            type="button"
                            disabled={remove.isPending}
                            onClick={() => handleRemove(row.psid)}
                            className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Remove
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pagination && (
          <div className="border-t border-gray-100 px-4 py-3">
            <Pagination
              total={pagination.total ?? rows.length}
              page={page}
              itemsPerPage={limit}
              limit={limit}
              setPage={handlePageChange}
              setItemsPerPage={handleLimitChange}
              showButtons
              showSelect
            />
          </div>
        )}
      </div>
    </div>
  );
}
